import { useState, useEffect } from 'react'
import {
  LayoutDashboard,
  Users,
  BarChart3,
  Settings,
  FileText,
  ChevronLeft,
  ChevronDown,
  LogOut,
  UserPlus,
  UserCog,
  Shield,
  BarChart,
  TrendingUp,
  PieChart,
  FileBarChart,
  CardSim,
  CreditCard,
  X,
  ChevronRight,
  CalendarDays,
  Banknote,
  MonitorCheck,
  BitcoinIcon,
  CoinsIcon,
  Coins,
  BookCheck
} from 'lucide-react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { colors } from '../../Constant/Color'
import { handleLogout, hasPermission } from '../../utils'
import { getCahbookUser } from '../../Storage/Storage'

function Sidebar({ isCollapsed: externalCollapsed, onToggleCollapse, setHideSideBar, onMobileClose, isMobile }) {
  const user = getCahbookUser()

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [internalCollapsed, setInternalCollapsed] = useState(false)
  const [activePath, setActivePath] = useState(window.location.pathname)

  // Use external collapsed state if provided, otherwise use internal
  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed
  const toggleCollapse = onToggleCollapse || (() => setInternalCollapsed(!internalCollapsed))

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/',
      module: 'dashboard',
    },
    {
      icon: Shield,
      label: 'Roles',
      path: '/role',
      module: 'rolesAndPermissions',


    },
    {
      icon: Banknote,
      label: 'Accounts',
      path: '/account',
      module: 'account',



    },
     {
      icon: BookCheck,
      label: 'Books',
      path: '/book',
      module: 'book',
    },
    {
      icon: Users,
      label: 'Users',
      path: '/users',
      module: 'user',



    },
   
    {
      icon: Coins,
      label: 'Cashbook',
      path: '/cashbook',
      module: 'cashbook',
    },



  ]

  const [expandedItems, setExpandedItems] = useState([])

  // Filter menu items based on permissions
  const getVisibleMenuItems = () => {
    return menuItems.filter(item => {
      if (!item.module) return true;
      const userHasPermission = hasPermission(item.module, 'read');
      if (!userHasPermission) return false;
      if (item.children) {
        const visibleChildren = item.children.filter(child =>
          !child.module ? true : hasPermission(child.module, 'read')
        );
        if (visibleChildren.length > 0) {
          item.children = visibleChildren;
          return true;
        }
        return false;
      }
      return true;
    });
  };

  const visibleMenuItems = getVisibleMenuItems();

  // Hide sidebar if only one menu item
  useEffect(() => {
    if (setHideSideBar) {
      if (visibleMenuItems.length === 1) {
        setHideSideBar(true);
      } else {
        setHideSideBar(false);
      }
    }
  }, [visibleMenuItems, setHideSideBar]);

  // Auto-expand parent menu if child is active
  useEffect(() => {
    visibleMenuItems.forEach((item, index) => {
      if (item.children && item.children.some(child => child.path === activePath)) {
        setExpandedItems(prev => prev.includes(index) ? prev : [...prev, index])
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath])

  // Handle navigation click - close mobile sidebar
  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  }

  const toggleExpand = (index) => {
    setExpandedItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  const isActive = (path) => {
    return activePath === path
  }

  const hasActiveChild = (children) => {
    if (!children) return false
    return children.some(child => activePath === child.path)
  }

  return (
    <div
      className={`${isMobile ? 'w-64' : (isCollapsed ? 'w-20' : 'w-64')
        } bg-white border-r border-gray-200 text-gray-800 h-full transition-all duration-300 ease-in-out relative shadow-sm`}
    >
      {/* Toggle Button */}
      <button
        onClick={toggleCollapse}
        className="absolute -right-3 top-6 bg-white text-gray-600 rounded-full p-1.5 hover:bg-gray-50 hover:text-gray-800 transition-colors border-2 border-gray-200 shadow-md z-10"
      >
        {isMobile ? (
          <X className="w-4 h-4" />
        ) : (
          isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      <div className="p-4 border-b border-gray-100">
        <div className='flex justify-center md:justify-start items-center'>
          {(!isCollapsed || isMobile) ? (
            <img
              src={require('../../Images/logo.png')}
              alt="HeadsUp Corporation"
              className="h-11 object-contain px-5"
            />
          ) : (
            <img
              src="/logo192.png"
              alt="Logo"
              className="h-10 m-0.5 p-1 object-contain"
            />
          )}
        </div>
      </div>

      <nav className="mt-6">
        <ul className="space-y-1 px-3">
          {visibleMenuItems.map((item, index) => {
            const Icon = item.icon
            const isExpanded = expandedItems.includes(index)
            const hasChildren = item.children && item.children.length > 0
            const itemIsActive = isActive(item.path)
            const childIsActive = hasActiveChild(item.children)

            return (
              <li key={index}>
                {hasChildren ? (
                  <div>
                    <button
                      onClick={() => {
                        if (isCollapsed && !isMobile) {
                          toggleCollapse()
                          setTimeout(() => toggleExpand(index), 300)
                        } else {
                          toggleExpand(index)
                        }
                      }}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all duration-200 group"
                      style={{
                        backgroundColor: childIsActive ? colors.PRIMARY_LIGHTER : 'transparent',
                        color: childIsActive ? colors.PRIMARY : colors.TEXT_SECONDARY,
                        borderLeft: childIsActive ? `4px solid ${colors.PRIMARY}` : 'none',
                      }}
                      onMouseEnter={(e) => {
                        if (!childIsActive) {
                          e.currentTarget.style.backgroundColor = colors.PRIMARY_LIGHTER;
                          e.currentTarget.style.color = colors.PRIMARY;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!childIsActive) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = colors.TEXT_SECONDARY;
                        }
                      }}
                      title={isCollapsed && !isMobile ? item.label : ''}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {(!isCollapsed || isMobile) && (
                          <span className="font-medium text-sm">{item.label}</span>
                        )}
                      </div>
                      {(!isCollapsed || isMobile) && (
                        <ChevronDown
                          className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                            }`}
                        />
                      )}
                    </button>

                    {/* Submenu */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                    >
                      {(!isCollapsed || isMobile) && (
                        <ul className="mt-1 space-y-1 ml-4">
                          {item.children.map((child, childIndex) => {
                            const ChildIcon = child.icon
                            const childActive = isActive(child.path)
                            return (
                              <li key={childIndex}>
                                <NavLink
                                  to={child.path}
                                  onClick={() => {
                                    setActivePath(child.path);
                                    handleNavClick();
                                  }}
                                  className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200"
                                  style={{
                                    backgroundColor: childActive ? colors.PRIMARY_LIGHT : 'transparent',
                                    color: childActive ? colors.PRIMARY : colors.TEXT_SECONDARY,
                                    borderLeft: childActive ? `4px solid ${colors.PRIMARY}` : 'none',
                                    fontWeight: childActive ? '600' : '500',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!childActive) {
                                      e.currentTarget.style.backgroundColor = colors.PRIMARY_LIGHTER;
                                      e.currentTarget.style.color = colors.PRIMARY;
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!childActive) {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                      e.currentTarget.style.color = colors.TEXT_SECONDARY;
                                    }
                                  }}
                                >
                                  <ChildIcon className="w-4 h-4 flex-shrink-0" />
                                  <span className="font-medium text-sm">{child.label}</span>
                                </NavLink>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    onClick={() => {
                      setActivePath(item.path);
                      handleNavClick();
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group"
                    style={{
                      backgroundColor: itemIsActive ? colors.PRIMARY_LIGHTER : 'transparent',
                      color: itemIsActive ? colors.PRIMARY : colors.TEXT_SECONDARY,
                      borderLeft: itemIsActive ? `4px solid ${colors.PRIMARY}` : 'none',
                      fontWeight: itemIsActive ? '600' : '500',
                    }}
                    onMouseEnter={(e) => {
                      if (!itemIsActive) {
                        e.currentTarget.style.backgroundColor = colors.PRIMARY_LIGHTER;
                        e.currentTarget.style.color = colors.PRIMARY;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!itemIsActive) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = colors.TEXT_SECONDARY;
                      }
                    }}
                    title={isCollapsed && !isMobile ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {(!isCollapsed || isMobile) && (
                      <span className="font-medium text-sm">{item.label}</span>
                    )}
                  </NavLink>
                )}
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 px-3">
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl border transition-colors"
          style={{
            backgroundColor: colors.BG_GRAY,
            borderColor: colors.BORDER_GRAY
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.BORDER_GRAY}
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${colors.PRIMARY} 0%, ${colors.SECONDARY} 100%)`
            }}
          >
            <span style={{ color: colors.BG_WHITE }} className="text-sm font-semibold">  {user?.name?.charAt(0)?.toUpperCase()}</span>
          </div>
          {(!isCollapsed || isMobile) && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{user?.name || "user"}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email || "example.com"}</p>
              </div>
              <button
                className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors text-gray-500 hover:text-red-500"
                title="Logout"
                onClick={() => handleLogout(navigate, dispatch)}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Sidebar
